import type { ApiClient } from "../../types/UserTypes";
import type { components } from "../../types/api";
import { assertReportAdmission } from "../reportsV3/nativeConditions.mjs";

export type ReportAssetV3 = components["schemas"]["ReportAssetV3"];
export type ReportExecuteResponseV3 = components["schemas"]["ReportExecuteResponseV3"];

export type FormattedInvoicePayload = {
  definitionId?: string | null;
  schemaVersion?: string | null;
  definitionVersion?: number;
  metadata: Record<string, unknown>;
  assets: Record<string, ReportAssetV3>;
  datasets: Record<string, unknown>;
  layout: unknown;
};

export type FormattedInvoiceTableRow = Record<string, unknown>;

export type FormattedInvoiceTable = {
  title: string;
  rows: FormattedInvoiceTableRow[];
  emptyLabel?: string;
};

const DATASET_NAMES = {
  invoiceHeader: ["invoiceHeader", "InvoiceHeader"],
  brokerSummary: ["brokerSummary", "BrokerSummary"],
  prospectSummary: ["prospectSummary", "ProspectSummary"],
  landmanDetails: ["landmanDetails", "LandmanDetails"],
  invoiceTotals: ["invoiceTotals", "InvoiceTotals"],
  expenseColumns: ["expenseColumns", "ExpenseColumns"],
};

export const FORMATTED_INVOICE_DEFINITION_ID = "invoice-formatted-v1";

export function toFormattedInvoicePayload(response: ReportExecuteResponseV3): FormattedInvoicePayload {
  assertReportAdmission(response);
  if (response.schemaVersion === "reports-v3.1" && (!isRecord(response.datasets) || !isRecord(response.layout) || !Array.isArray(response.layout.bands) || (response.assets != null && !isRecord(response.assets)))) {
    throw new Error("Native execution must return dataset/asset dictionaries and layout.bands. No sample data has been substituted.");
  }
  return {
    definitionId: response.definitionId,
    schemaVersion: response.schemaVersion,
    definitionVersion: response.definitionVersion,
    metadata: asRecord(response.metadata),
    assets: asAssetRecord(response.assets),
    datasets: asRecord(response.datasets),
    layout: response.layout,
  };
}

export function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

export function getDataset(payload: FormattedInvoicePayload, key: keyof typeof DATASET_NAMES): unknown {
  const names = DATASET_NAMES[key];
  for (const name of names) {
    if (payload.datasets[name] !== undefined) return payload.datasets[name];
  }
  return undefined;
}

export function getRows(value: unknown): FormattedInvoiceTableRow[] {
  if (Array.isArray(value)) return value.filter(isRecord) as FormattedInvoiceTableRow[];
  const record = asRecord(value);
  for (const key of ["rows", "Rows", "items", "Items", "entries", "Entries", "details", "Details"]) {
    const maybeRows = record[key];
    if (Array.isArray(maybeRows)) return maybeRows.filter(isRecord) as FormattedInvoiceTableRow[];
  }
  return [];
}

export function getFirstRecord(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) return asRecord(value.find(isRecord));
  return asRecord(value);
}

export function getValue(record: Record<string, unknown>, keys: string[], fallback = ""): string {
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") return String(value);
  }
  return fallback;
}

export function getNumber(record: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return 0;
}

export function formatCurrency(value: unknown): string {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number.isFinite(num) ? num : 0,
  );
}

export function formatPlainValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function titleizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function tableColumns(rows: FormattedInvoiceTableRow[], preferred: string[] = []): string[] {
  const keys = new Set<string>();
  rows.forEach((row) => Object.keys(row).forEach((key) => {
    if (!isHiddenColumn(key)) keys.add(key);
  }));

  const ordered: string[] = [];
  preferred.forEach((key) => {
    if (keys.has(key)) {
      ordered.push(key);
      keys.delete(key);
    }
  });
  return [...ordered, ...Array.from(keys)];
}

export function isMoneyColumn(key: string): boolean {
  return /total|amount|cost|rate|fee|services|expense|due|balance/i.test(key);
}

export function formattedInvoiceHasData(payload: FormattedInvoicePayload): boolean {
  return [
    getRows(getDataset(payload, "brokerSummary")),
    getRows(getDataset(payload, "prospectSummary")),
    getRows(getDataset(payload, "landmanDetails")),
  ].some((rows) => rows.length > 0);
}

export function buildFormattedInvoiceTables(payload: FormattedInvoicePayload): FormattedInvoiceTable[] {
  return [
    {
      title: "Broker Summary",
      rows: getRows(getDataset(payload, "brokerSummary")),
      emptyLabel: "No broker summary rows returned.",
    },
    {
      title: "Prospect Summary",
      rows: getRows(getDataset(payload, "prospectSummary")),
      emptyLabel: "No prospect summary rows returned.",
    },
    {
      title: "Landman Details",
      rows: getRows(getDataset(payload, "landmanDetails")),
      emptyLabel: "No landman detail rows returned.",
    },
  ];
}

export async function loadReportAssetObjectUrl(api: ApiClient, asset?: ReportAssetV3 | null): Promise<string | null> {
  if (!asset?.available || !asset.url) return null;
  if (isExternalAssetUrl(asset.url)) return asset.url;
  const blob = await loadReportAssetBlob(api, asset.url);
  return URL.createObjectURL(blob);
}

export async function loadReportAssetBlob(api: ApiClient, pathOrUrl: string): Promise<Blob> {
  if (isExternalAssetUrl(pathOrUrl)) {
    throw new Error("External report asset URLs cannot be fetched by the browser export path.");
  }

  return api.getBlob(normalizeAssetPath(pathOrUrl));
}

export function normalizeAssetPath(pathOrUrl: string): string {
  try {
    const url = new URL(pathOrUrl, window.location.origin);
    return url.pathname.replace(/^\/api\/?/i, "");
  } catch {
    return String(pathOrUrl).replace(/^\/?api\/?/i, "");
  }
}

export function companyLogoAsset(payload: FormattedInvoicePayload): ReportAssetV3 | undefined {
  return payload.assets["company.logo"] || payload.assets.companyLogo || payload.assets.logo;
}

export function isExternalAssetUrl(pathOrUrl: string): boolean {
  try {
    const url = new URL(pathOrUrl);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isHiddenColumn(key: string): boolean {
  return /^(id|tenantId|tenantID|clientId|invoiceId|expenses)$/i.test(key);
}

function asAssetRecord(value: unknown): Record<string, ReportAssetV3> {
  const record = asRecord(value);
  return Object.fromEntries(
    Object.entries(record).filter(([, asset]) => asset && typeof asset === "object"),
  ) as Record<string, ReportAssetV3>;
}
